<script setup lang="ts">
import { ref, computed, Ref, watch } from 'vue';
import pMap from 'p-map';
import { useEditUser, CustomerMgmtTypes, useSearchUsers } from '@ilteducation/customer-mgmt';
import useModal, { ModalTypes, modals } from '../../composables/useModal';
import { retryBooleanPromise } from '../../utils';
import HeadingWithIcon from '../HeadingWithIcon.vue';
import Modal from '../Modal.vue';
import SetGroupPasswordForm from './SetGroupPasswordForm.vue';
import { useLocale } from '../../composables/useLocale';

const { t } = useLocale();

const props = defineProps<{
  name: ModalTypes;
  group: CustomerMgmtTypes.GetGroupResponse;
}>();

const { updateUser } = useEditUser();
const { fetchAllGroupMemebers } = useSearchUsers();

type Members = CustomerMgmtTypes.SearchUserOutput[];

enum States {
  GET_PASSWORD,
  SETTING_PASSWORD,
  SET_PASSWORD_SUCCESS,
  SET_PASSWORD_FAILED,
}

const state = ref<States>(States.GET_PASSWORD);
const password = ref('');
const setPassword = (newPassword: string) => {
  password.value = newPassword;
};

const passwordFormRef = ref();

const { isOpen, close } = useModal(props.name);
const { open: openEditGroupLoginModal } = useModal(modals.EDIT_GROUP_LOGIN);
const changingPassword = ref(false);
const retries = ref(1);
const maxRetries = 3;

const preventReload = (e: BeforeUnloadEvent) => {
  e.preventDefault();
};

watch(isOpen, (newValue, oldValue) => {
  if (newValue && !oldValue) {
    state.value = States.GET_PASSWORD;
  }
});

const isFetchingUsersLoading = ref<boolean>(false);
watch(
  [() => props.group.uid, isOpen],
  async ([groupUid, isThisOpen]: [string, boolean]) => {
    if (isThisOpen) {
      isFetchingUsersLoading.value = true;
      members.value = await fetchAllGroupMemebers(groupUid);
      isFetchingUsersLoading.value = false;
    }
  },
  { immediate: true },
);

const progress = ref(0);
const updatedUsers: Ref<Members> = ref([]);
const unUpdatableUsers: Ref<Members> = ref([]);
const failedUsers: Ref<Members> = ref([]);
const members = ref<Members>([]);
const changePassword = async (failedMembers: Members = []) => {
  let membersToChange: Members;

  if (failedMembers?.length) {
    membersToChange = failedMembers;
  } else {
    state.value = States.SETTING_PASSWORD;
    membersToChange = members.value;
  }

  if (!membersToChange || membersToChange.length === 0 || !password.value) {
    state.value = States.GET_PASSWORD;
    return;
  }

  changingPassword.value = true;

  // Prevent page reload, only while updating passwords
  window.addEventListener('beforeunload', preventReload);

  let index = 0;
  await pMap(
    membersToChange,
    async (user) => {
      const res = await setUserPassword(user);
      index += 1;
      progress.value = Math.round((index / membersToChange.length) * 100);
      return res;
    },
    { concurrency: 5 },
  );

  if (failedUsers.value.length > 0) {
    state.value = States.SET_PASSWORD_FAILED;
    changingPassword.value = false;
    window.removeEventListener('beforeunload', preventReload);
    return;
  }

  changingPassword.value = false;
  state.value = States.SET_PASSWORD_SUCCESS;
  window.removeEventListener('beforeunload', preventReload);
};

const retryFailedUsers = async () => {
  retries.value += 1;
  const originalFailedUsers = failedUsers.value;
  failedUsers.value = [];
  progress.value = 0;
  await changePassword(originalFailedUsers);
  // If we have retried the max number of times and still have failed users, show the error
  if (failedUsers.value.length > 0 && retries.value >= maxRetries) {
    state.value = States.SET_PASSWORD_FAILED;
    changingPassword.value = false;
    return;
  }
  return;
};

const isUserStudent = (user: Members[number]) => {
  if (!user) {
    return false;
  }

  if (Array.isArray(user.associations)) {
    return !user.associations.some((unit) => unit.role !== 'student');
  }

  return false;
};

const setUserPassword = async (user: Members[number]) => {
  if (!isUserStudent(user)) {
    unUpdatableUsers.value.push(user);
    return true;
  }

  const success = await retryBooleanPromise(() => updateUser(user.uid, { password: password.value }));
  if (!success) {
    failedUsers.value.push(user);
    return false;
  }

  updatedUsers.value.push(user);
  return true;
};

const onCancel = () => {
  changingPassword.value = false;
  window.removeEventListener('beforeunload', preventReload);
  progress.value = 0;
  updatedUsers.value = [];
  unUpdatableUsers.value = [];
  failedUsers.value = [];
  state.value = States.GET_PASSWORD;
  password.value = '';
  retries.value = 0;
  close();
  openEditGroupLoginModal();
};

const getTitle = computed(() => {
  switch (state.value) {
    case States.GET_PASSWORD:
      return t('lcp.new_password');
    case States.SETTING_PASSWORD:
      return t('lcp.applying_password', { count: members.value?.length });
    case States.SET_PASSWORD_SUCCESS:
      return t('lcp.password_updated');
    case States.SET_PASSWORD_FAILED:
      return t('lcp.password_update_failed');
    default:
      return t('lcp.new_password');
  }
});
const getActionLabel = computed(() => {
  switch (state.value) {
    case States.SETTING_PASSWORD:
      return '';
    case States.SET_PASSWORD_SUCCESS:
      return t('lcp.done');
    case States.SET_PASSWORD_FAILED:
      return retries.value < maxRetries ? t('lcp.retry_setting_password') : t('lcp.close');
    default:
      return t('lcp.set_password');
  }
});
const getAction = computed(() => {
  switch (state.value) {
    case States.GET_PASSWORD:
      return changePassword;
    case States.SETTING_PASSWORD:
      return undefined;
    case States.SET_PASSWORD_SUCCESS:
      return onCancel;
    case States.SET_PASSWORD_FAILED:
      return retries.value < maxRetries ? retryFailedUsers : onCancel;
    default:
      return changePassword;
  }
});
const getCancelAction = computed(() => {
  switch (state.value) {
    case States.GET_PASSWORD:
      return onCancel;
    case States.SETTING_PASSWORD:
      return undefined;
    case States.SET_PASSWORD_SUCCESS:
      return undefined;
    case States.SET_PASSWORD_FAILED:
      return retries.value < maxRetries ? onCancel : undefined;
    default:
      return onCancel;
  }
});

const isLoading = computed(() => changingPassword.value || isFetchingUsersLoading.value);
</script>

<template>
  <Modal
    :name="name"
    :title="getTitle"
    :is-open="isOpen"
    show-close-btn
    width="560"
    :action-label="getActionLabel"
    :action="getAction"
    :action-disabled="isLoading || (passwordFormRef && !passwordFormRef.isValid)"
    :loading="isLoading"
    :cancel-action="getCancelAction"
    :cancel-action-label="t('lcp.back')"
    :showCancelAction="getCancelAction && state !== States.SETTING_PASSWORD"
  >
    <SetGroupPasswordForm
      ref="passwordFormRef"
      v-if="state === States.GET_PASSWORD"
      :user-count="members ? members.length : 0"
      :change-password="changePassword"
      :loading="isLoading"
      @set-password="setPassword"
    />

    <v-row v-else-if="state === States.SETTING_PASSWORD" no-gutters>
      <v-col cols="12" class="text-center">
        <p class="pb-2">{{ t('lcp.in_progress') }}</p>
        {{ progress }}%
        <v-progress-linear v-model="progress" color="primary" height="8"></v-progress-linear>
      </v-col>
    </v-row>

    <v-row v-else-if="state > 1" no-gutters class="ga-6">
      <v-col cols="12">
        <p>{{ t('lcp.password_success_users_in_group', { updated: updatedUsers?.length, total: members?.length }) }}</p>
      </v-col>
      <v-col v-if="state === States.SET_PASSWORD_FAILED" cols="12">
        <HeadingWithIcon
          icon="mdi-alert"
          icon-color="error"
          :heading="t('lcp.failed_to_update_password', { count: failedUsers.length })"
          class="pa-0"
        />
        <p>{{ failedUsers.map((u) => u.fullname).join(', ') }}</p>
      </v-col>
      <v-col v-if="unUpdatableUsers.length" cols="12">
        <HeadingWithIcon
          icon="mdi-information-slab-circle"
          icon-color="rgb(var(--v-theme-on-surface-medium)"
          :heading="t('lcp.not_affected', { count: unUpdatableUsers.length })"
          class="pa-0"
        />
        <p>{{ unUpdatableUsers.map((u) => u.fullname).join(', ') }}</p>
      </v-col>
      <v-col v-if="failedUsers.length > 0 && retries >= maxRetries" cols="12">
        <v-alert type="error" variant="outlined" border="start" icon="mdi-alert">
          {{ t('lcp.password_update_error') }}
        </v-alert>
      </v-col>
    </v-row>
  </Modal>
</template>
